import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import apiKeysService from "../features/apiKeys/apiKeysService";
import organisationService from "../features/organisation/organisationService";

const formatDate = (value) =>
  value ? new Date(value).toLocaleString() : "-";

/** "2026-10-12" -> "12 Oct 2026", without dragging in a date library. */
const formatDay = (value) => {
  if (!value) return "-";
  const date = new Date(`${value}T00:00:00`);
  return Number.isNaN(date.getTime())
    ? value
    : date.toLocaleDateString(undefined, {
        day: "numeric",
        month: "short",
        year: "numeric",
      });
};

const MCP_SCOPE_LABELS = {
  rankings: "Rankings",
  admin: "Admin",
};

const MCP_SCOPE_HELP = {
  rankings:
    "Ranking and reference tools only - the same data the public ranking pages already publish. Safe to hand to an external user or organisation.",
  admin:
    "Everything in Rankings, plus player lookups, entry search, flags and read-only SQL. For internal use only.",
};

/**
 * API keys for machine clients.
 *
 * Two things a key can do: authenticate the BowlScout import and lookups, and -
 * only once an admin grants it - connect an external AI client to the hosted
 * MCP endpoint. The two are independent, and MCP access is off by default, so
 * the key the import already uses keeps working untouched.
 *
 * Uses local state rather than a Redux slice: nothing else in the app reads
 * this data, and it only changes when an admin is on this page.
 *
 * The plaintext of a key exists in exactly one response and is never stored,
 * so the reveal dialog after creating one is the only chance to copy it.
 */
export default function ApiKeys() {
  const [apiKeys, setApiKeys] = useState([]);
  const [organisations, setOrganisations] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const [createOpen, setCreateOpen] = useState(false);
  const [name, setName] = useState("");
  const [newScope, setNewScope] = useState("");
  const [newOrganisationId, setNewOrganisationId] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [formError, setFormError] = useState("");

  const [revealedKey, setRevealedKey] = useState("");
  const [copied, setCopied] = useState(false);
  const keyInputRef = useRef(null);

  const [revokeTarget, setRevokeTarget] = useState(null); // key pending revoke
  const [configTarget, setConfigTarget] = useState(null); // key being configured
  const [configScope, setConfigScope] = useState("");
  const [configOrganisationId, setConfigOrganisationId] = useState("");
  const [configError, setConfigError] = useState("");

  const [usageTarget, setUsageTarget] = useState(null);
  const [usage, setUsage] = useState(null);

  const organisationNames = useMemo(
    () => new Map(organisations.map((org) => [String(org.id), org.name])),
    [organisations],
  );

  const load = useCallback(async () => {
    setIsLoading(true);
    setError("");

    try {
      // Both are admin-only reads and the page needs them together - the
      // organisation column and the create dialog's picker both resolve names
      // from the same list.
      const [keysResponse, organisationsResponse] = await Promise.all([
        apiKeysService.listApiKeys(),
        organisationService.listOrganisations(),
      ]);

      setApiKeys(keysResponse.data.apiKeys || []);
      setOrganisations(organisationsResponse.data.organisations || []);
    } catch (err) {
      setError(
        err.response?.data?.message || err.message || "Failed to load API keys",
      );
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openCreate = () => {
    setName("");
    setNewScope("");
    setNewOrganisationId("");
    setFormError("");
    setCreateOpen(true);
  };

  const create = async () => {
    setIsSaving(true);
    setFormError("");

    try {
      const response = await apiKeysService.createApiKey({
        name,
        mcp_scope: newScope || null,
        // Only a rankings-tier key can be bound to an organisation; the server
        // rejects the combination outright, so it is never sent.
        organisation_id:
          newScope === "rankings" && newOrganisationId
            ? Number(newOrganisationId)
            : null,
      });

      setCreateOpen(false);
      setNotice(`Created ${response.data.apiKey?.name || name}`);
      setRevealedKey(response.data.key || "");
      setCopied(false);
      await load();
    } catch (err) {
      setFormError(
        err.response?.data?.message || err.message || "Failed to create key",
      );
    } finally {
      setIsSaving(false);
    }
  };

  const openConfig = (key) => {
    setConfigTarget(key);
    setConfigScope(key.mcp_scope || "");
    setConfigOrganisationId(
      key.organisation_id ? String(key.organisation_id) : "",
    );
    setConfigError("");
  };

  const saveConfig = async () => {
    setIsSaving(true);
    setConfigError("");

    try {
      await apiKeysService.updateApiKeyMcp(configTarget.id, {
        mcp_scope: configScope || null,
        organisation_id:
          configScope === "rankings" && configOrganisationId
            ? Number(configOrganisationId)
            : null,
      });

      setNotice(
        configScope
          ? `MCP access set to ${MCP_SCOPE_LABELS[configScope]} for ${configTarget.name}`
          : `MCP access removed from ${configTarget.name}`,
      );
      setConfigTarget(null);
      await load();
    } catch (err) {
      setConfigError(
        err.response?.data?.message || err.message || "Failed to save",
      );
    } finally {
      setIsSaving(false);
    }
  };

  const openUsage = async (key) => {
    setUsageTarget(key);
    setUsage(null);

    try {
      const response = await apiKeysService.getApiKeyUsage(key.id);
      setUsage(response.data);
    } catch (err) {
      setError(
        err.response?.data?.message || err.message || "Failed to load usage",
      );
      setUsageTarget(null);
    }
  };

  const copyKey = async () => {
    try {
      await navigator.clipboard.writeText(revealedKey);
      setCopied(true);
    } catch {
      // Clipboard API needs a secure context; fall back to selecting the text
      // so the key can still be copied by hand.
      keyInputRef.current?.focus();
      keyInputRef.current?.select();
      try {
        document.execCommand("copy");
        setCopied(true);
      } catch {
        /* leave it selected for a manual copy */
      }
    }
  };

  const confirmRevoke = async () => {
    setIsSaving(true);

    try {
      await apiKeysService.revokeApiKey(revokeTarget.id);
      setNotice(`Revoked ${revokeTarget.name}`);
      setRevokeTarget(null);
      await load();
    } catch (err) {
      setError(
        err.response?.data?.message || err.message || "Failed to revoke key",
      );
      setRevokeTarget(null);
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
          gap: 2,
        }}
      >
        <Typography variant="h4">API Keys</Typography>
        <Button variant="contained" onClick={openCreate}>
          Create API key
        </Button>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Keys authenticate the BowlScout import and lookup endpoints. A key can
        also be granted MCP access, which lets an external AI client query the
        rankings over the hosted MCP endpoint - off by default, and granted per
        key so one can be revoked without disturbing the others. A key is shown
        once when created and only its hash is stored, so it cannot be recovered
        later.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}

      {notice && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setNotice("")}>
          {notice}
        </Alert>
      )}

      {apiKeys.length === 0 ? (
        <Typography variant="body1" color="text.secondary" sx={{ py: 4 }}>
          No API keys yet. The machine endpoints are running on the
          BOWLSCOUT_API_KEY environment variable until one is created.
        </Typography>
      ) : (
        <Box sx={{ overflowX: "auto" }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Key</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>MCP access</TableCell>
                <TableCell>Organisation</TableCell>
                <TableCell align="right">Queries (30d)</TableCell>
                <TableCell>Created</TableCell>
                <TableCell>Last used</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {apiKeys.map((key) => (
                <TableRow key={key.id} hover>
                  <TableCell>{key.name}</TableCell>
                  <TableCell sx={{ fontFamily: "monospace" }}>
                    {key.prefix}••••••••
                  </TableCell>
                  <TableCell>
                    {key.is_active ? (
                      <Chip size="small" color="success" label="Active" />
                    ) : (
                      <Chip size="small" label="Revoked" />
                    )}
                  </TableCell>
                  <TableCell>
                    {key.mcp_scope ? (
                      <Chip
                        size="small"
                        color={key.mcp_scope === "admin" ? "warning" : "primary"}
                        label={MCP_SCOPE_LABELS[key.mcp_scope] || key.mcp_scope}
                      />
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        —
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {key.mcp_scope
                      ? organisationNames.get(String(key.organisation_id)) ||
                        "All organisations"
                      : "—"}
                  </TableCell>
                  <TableCell align="right">
                    {key.mcp_scope ? key.queries_30d ?? 0 : "—"}
                  </TableCell>
                  <TableCell>{formatDate(key.created_at)}</TableCell>
                  <TableCell>{formatDate(key.last_used_at)}</TableCell>
                  <TableCell align="right">
                    <Button size="small" onClick={() => openConfig(key)}>
                      MCP
                    </Button>
                    <Button
                      size="small"
                      disabled={!key.mcp_scope}
                      onClick={() => openUsage(key)}
                    >
                      Usage
                    </Button>
                    <Button
                      size="small"
                      color="error"
                      disabled={!key.is_active}
                      onClick={() => setRevokeTarget(key)}
                    >
                      Revoke
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}

      {/* Create */}
      <Dialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Create API key</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Name the client this key is for, so it can be identified later when
            it needs revoking.
          </Typography>

          {formError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {formError}
            </Alert>
          )}

          <TextField
            autoFocus
            fullWidth
            label="Name"
            value={name}
            onChange={(event) => setName(event.target.value)}
            inputProps={{ maxLength: 200 }}
            helperText="For example: BowlScout"
            sx={{ mb: 2 }}
          />

          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel id="new-key-mcp">MCP access</InputLabel>
            <Select
              labelId="new-key-mcp"
              label="MCP access"
              value={newScope}
              onChange={(event) => setNewScope(event.target.value)}
            >
              <MenuItem value="">None (machine endpoints only)</MenuItem>
              <MenuItem value="rankings">Rankings</MenuItem>
              <MenuItem value="admin">Admin (internal)</MenuItem>
            </Select>
          </FormControl>

          {newScope === "rankings" && (
            <FormControl fullWidth>
              <InputLabel id="new-key-org">Organisation</InputLabel>
              <Select
                labelId="new-key-org"
                label="Organisation"
                value={newOrganisationId}
                onChange={(event) => setNewOrganisationId(event.target.value)}
              >
                <MenuItem value="">All organisations (national)</MenuItem>
                {organisations.map((org) => (
                  <MenuItem key={org.id} value={String(org.id)}>
                    {org.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {newScope && (
            <Typography variant="caption" color="text.secondary">
              {MCP_SCOPE_HELP[newScope]}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)} disabled={isSaving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={create}
            disabled={isSaving || !name.trim()}
          >
            {isSaving ? "Creating..." : "Create"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Configure MCP access */}
      <Dialog
        open={configTarget !== null}
        onClose={() => setConfigTarget(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>MCP access — {configTarget?.name}</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ mb: 2 }}>
            This controls whether the key can be used against the hosted MCP
            endpoint. It has no effect on the import and lookup endpoints, which
            keep working either way.
          </Typography>

          {configError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {configError}
            </Alert>
          )}

          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel id="config-mcp">MCP access</InputLabel>
            <Select
              labelId="config-mcp"
              label="MCP access"
              value={configScope}
              onChange={(event) => setConfigScope(event.target.value)}
            >
              <MenuItem value="">None (machine endpoints only)</MenuItem>
              <MenuItem value="rankings">Rankings</MenuItem>
              <MenuItem value="admin">Admin (internal)</MenuItem>
            </Select>
          </FormControl>

          {configScope === "rankings" && (
            <FormControl fullWidth>
              <InputLabel id="config-org">Organisation</InputLabel>
              <Select
                labelId="config-org"
                label="Organisation"
                value={configOrganisationId}
                onChange={(event) =>
                  setConfigOrganisationId(event.target.value)
                }
              >
                <MenuItem value="">All organisations (national)</MenuItem>
                {organisations.map((org) => (
                  <MenuItem key={org.id} value={String(org.id)}>
                    {org.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {configScope && (
            <Typography variant="caption" color="text.secondary">
              {MCP_SCOPE_HELP[configScope]}
              {configScope === "rankings" &&
                " Bound to an organisation, the ranking tools return that organisation's published standings instead of the national ones."}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfigTarget(null)} disabled={isSaving}>
            Cancel
          </Button>
          <Button variant="contained" onClick={saveConfig} disabled={isSaving}>
            {isSaving ? "Saving..." : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Usage */}
      <Dialog
        open={usageTarget !== null}
        onClose={() => setUsageTarget(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Usage — {usageTarget?.name}</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            MCP queries per day over the last {usage?.days ?? 30} days. Only
            tool calls count — connecting and listing tools does not. Traffic to
            the import and lookup endpoints is not included.
          </Typography>

          {usage === null ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress size={24} />
            </Box>
          ) : usage.daily.length === 0 ? (
            <Typography variant="body1" color="text.secondary" sx={{ py: 3 }}>
              No queries in this window.
            </Typography>
          ) : (
            <>
              <Alert severity="info" sx={{ mb: 2 }}>
                {usage.total} {usage.total === 1 ? "query" : "queries"} in the
                last {usage.days} days.
              </Alert>
              <Box sx={{ maxHeight: 320, overflowY: "auto" }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Day</TableCell>
                      <TableCell align="right">Queries</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {usage.daily.map((row) => (
                      <TableRow key={row.day}>
                        <TableCell>{formatDay(row.day)}</TableCell>
                        <TableCell align="right">{row.query_count}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUsageTarget(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* One-time reveal - the key is not recoverable after this closes */}
      <Dialog
        open={Boolean(revealedKey)}
        onClose={() => setRevealedKey("")}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>API key created</DialogTitle>
        <DialogContent dividers>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Copy this key now. It is shown only once and cannot be recovered.
          </Alert>

          <TextField
            inputRef={keyInputRef}
            value={revealedKey}
            fullWidth
            InputProps={{ readOnly: true, sx: { fontFamily: "monospace" } }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRevealedKey("")}>Done</Button>
          <Button variant="contained" onClick={copyKey}>
            {copied ? "Copied" : "Copy"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Revoke confirmation */}
      <Dialog
        open={revokeTarget !== null}
        onClose={() => setRevokeTarget(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Revoke API key</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2">
            Revoke {revokeTarget?.name}? Requests using this key will be
            rejected immediately, including MCP requests. This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRevokeTarget(null)} disabled={isSaving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={confirmRevoke}
            disabled={isSaving}
          >
            {isSaving ? "Revoking..." : "Revoke"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
